import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, IndianRupee, Users, Clock, Loader2 } from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { KpiCard, KpiGrid } from "@/components/dashboard/KpiCard";
import { EmptyState } from "@/components/EmptyState";
import { format } from "date-fns";

const DEPARTMENTS = ["Casting", "Filing", "Setting", "Polish", "QC", "Tagging", "Dispatch"];
const UNITS = ["per_piece", "per_gram", "per_hour", "per_day"];

export default function WorkCostingPage() {
  const { user } = useAuth();
  const canSeeCost = ["admin", "super-admin", "manager", "accounts"].includes(user?.role);
  const [rates, setRates] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [entryForm, setEntryForm] = useState({ department: "Casting", pieces: "", weight: "", hours: "", unit: "per_piece", notes: "" });
  const [rateForm, setRateForm] = useState({ department: "Casting", unit: "per_piece", rate: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (canSeeCost) {
      const r = await api.get("/work-costing/rates").catch(()=>({data:{items:[]}}));
      setRates(r.data.items || []);
      const s = await api.get("/work-costing/summary").catch(()=>({data:null}));
      setSummary(s.data);
    }
    const e = await api.get("/work-costing/entries?limit=100").catch(()=>({data:{items:[]}}));
    setEntries(e.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const saveRate = async () => {
    if (!rateForm.rate) return toast.error("Enter a rate");
    try {
      await api.post("/work-costing/rates", { department: rateForm.department, unit: rateForm.unit, rate: Number(rateForm.rate), active: true });
      toast.success("Rate saved"); setRateForm({...rateForm, rate: ""}); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const logEntry = async () => {
    setBusy(true);
    try {
      const payload = { department: entryForm.department, unit: entryForm.unit,
        pieces: Number(entryForm.pieces || 0), weight: Number(entryForm.weight || 0), hours: Number(entryForm.hours || 0),
        notes: entryForm.notes || null };
      await api.post("/work-costing/entries", payload);
      toast.success("Entry logged"); setEntryForm({ ...entryForm, pieces: "", weight: "", hours: "", notes: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="work-costing-page">
      <PageHead eyebrow="Production · Costing" title="Per-Employee Work Costing"
        subtitle="Daily piece-count / weight / hours logged by each employee, rates by department."/>

      {canSeeCost && (
        <>
          <KpiGrid cols={4}>
            <KpiCard label="Entries" value={summary?.grand_total?.entries ?? 0} icon={Clock} tone="green"/>
            <KpiCard label="Pieces"  value={summary?.grand_total?.pieces ?? 0}  icon={Users} tone="green"/>
            <KpiCard label="Weight (g)" value={Number(summary?.grand_total?.weight || 0).toFixed(1)} icon={Users} tone="ivory"/>
            <KpiCard label="Total cost ₹" value={"₹"+Number(summary?.grand_total?.total_cost || 0).toLocaleString("en-IN")} icon={IndianRupee} tone="gold"/>
          </KpiGrid>

          <SectionHead title="Department rate book"/>
          <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Select value={rateForm.department} onValueChange={(v)=>setRateForm({...rateForm, department:v})}>
                <SelectTrigger className="h-11"><SelectValue/></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={rateForm.unit} onValueChange={(v)=>setRateForm({...rateForm, unit:v})}>
                <SelectTrigger className="h-11"><SelectValue/></SelectTrigger>
                <SelectContent>{UNITS.map(u=><SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Rate ₹" value={rateForm.rate} onChange={(e)=>setRateForm({...rateForm, rate:e.target.value})} className="h-11"/>
              <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11" onClick={saveRate} data-testid="save-rate-btn"><Plus size={14} className="mr-1"/>Save rate</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {rates.map((r)=>(
                <span key={r.id} className="inline-flex items-center gap-2 rounded-full bg-[#E1F0E6] text-[#0F5E39] px-3 py-1 text-xs">
                  {r.department} · {r.unit} → ₹{r.rate}
                </span>
              ))}
              {rates.length === 0 && <span className="text-xs text-[#4A5A50]">No rates set. Add one above.</span>}
            </div>
          </CardContent></Card>
        </>
      )}

      <SectionHead title={canSeeCost ? "Log an entry (any employee)" : "Log my work"}/>
      <Card className="border-[#E4DFCF] rounded-2xl"><CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <Select value={entryForm.department} onValueChange={(v)=>setEntryForm({...entryForm, department:v})}>
            <SelectTrigger className="h-11"><SelectValue/></SelectTrigger>
            <SelectContent>{DEPARTMENTS.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={entryForm.unit} onValueChange={(v)=>setEntryForm({...entryForm, unit:v})}>
            <SelectTrigger className="h-11"><SelectValue/></SelectTrigger>
            <SelectContent>{UNITS.map(u=><SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" placeholder="Pieces" value={entryForm.pieces} onChange={(e)=>setEntryForm({...entryForm, pieces:e.target.value})} className="h-11"/>
          <Input type="number" placeholder="Weight g" value={entryForm.weight} onChange={(e)=>setEntryForm({...entryForm, weight:e.target.value})} className="h-11"/>
          <Input type="number" placeholder="Hours" value={entryForm.hours} onChange={(e)=>setEntryForm({...entryForm, hours:e.target.value})} className="h-11"/>
          <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11" onClick={logEntry} disabled={busy} data-testid="log-entry-btn">
            {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Plus size={14} className="mr-1"/>}Log
          </Button>
        </div>
      </CardContent></Card>

      <SectionHead title="Recent entries"/>
      {entries.length === 0 ? (
        <EmptyState icon={Clock} eyebrow="Nothing yet" title="No work entries" description="Log your first entry above to see it appear here and roll up into department cost totals."/>
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden"><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">User</th>
              <th className="p-3 text-left">Dept</th>
              <th className="p-3 text-left">Qty</th>
              {canSeeCost && <th className="p-3 text-left">Rate</th>}
              {canSeeCost && <th className="p-3 text-right">Cost</th>}
            </tr></thead>
            <tbody>{entries.map((e)=>(
              <tr key={e.id} className="border-t border-[#E4DFCF]">
                <td className="p-3 text-xs">{e.date ? format(new Date(e.date), "d MMM yyyy") : "—"}</td>
                <td className="p-3">{e.user_name || "—"}</td>
                <td className="p-3 text-xs">{e.department}</td>
                <td className="p-3 text-xs">{e.pieces > 0 ? `${e.pieces} pcs` : e.weight > 0 ? `${e.weight} g` : e.hours > 0 ? `${e.hours} h` : "—"}</td>
                {canSeeCost && <td className="p-3 font-mono text-xs">₹{e.rate}</td>}
                {canSeeCost && <td className="p-3 font-mono text-right">₹{Number(e.total_cost || 0).toLocaleString("en-IN")}</td>}
              </tr>
            ))}</tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
}
